/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * A2UI server_to_client 메시지 보정 파이프라인. LLM 산출물의 흔한 실수를 클라이언트에서
 * 방어적으로 교정한다(백엔드/모델 무관, 순수 함수 — defense in depth).
 */

/**
 * createSurface 메시지의 catalogId 를 실제 등록된 카탈로그 id 로 강제한다.
 *
 * 우리는 basicCatalog 하나만 등록하므로(신뢰 카탈로그 allowlist), 에이전트가 "basic" 같은 축약 id 를
 * 보내도 렌더러의 "Catalog not found" 를 피하고 확실히 렌더된다. 원본 배열/객체는 변형하지 않고
 * 교체가 필요한 메시지만 얕은 복사한다.
 */
export function normalizeCatalogId(
  messages: Record<string, unknown>[],
  catalogId: string,
): Record<string, unknown>[] {
  return messages.map((msg) => {
    const cs = (msg as any)?.createSurface;
    if (cs && typeof cs === 'object' && cs.catalogId !== catalogId) {
      return { ...msg, createSurface: { ...cs, catalogId } };
    }
    return msg;
  });
}

/**
 * 이미 존재하는 표면에 대한 createSurface 는 드롭한다. 멀티턴에서 에이전트가 같은 surfaceId 로
 * createSurface 를 다시 보내면 프로세서가 "Surface already exists" 로 throw 하는데, 표면은 유지하고
 * 뒤따르는 updateComponents 로 내용만 교체하면 되므로 중복 생성 메시지를 걸러낸다.
 *
 * 프로세서에 이미 있는 표면뿐 아니라 **같은 배치 안의 중복 createSurface** 도 거른다. 리듀서가
 * 멀티턴 stateful 표면의 히스토리를 한 턴으로 re-anchor 하면 같은 surfaceId 의 createSurface 가
 * 한 배치에 둘 이상 모일 수 있어(처음 것만 유효), 이를 처리하지 않으면 processMessages 가 throw 한다.
 */
export function dropDuplicateCreateSurface(
  messages: Record<string, unknown>[],
  existingSurfaceIds: Set<string>,
): Record<string, unknown>[] {
  const seen = new Set(existingSurfaceIds);
  return messages.filter((msg) => {
    const cs = (msg as any)?.createSurface;
    if (cs && typeof cs === 'object' && typeof cs.surfaceId === 'string') {
      if (seen.has(cs.surfaceId)) return false;
      seen.add(cs.surfaceId);
    }
    return true;
  });
}

/**
 * Button 의 `variant`(primary/borderless)를 **버튼 라벨 텍스트** 로 매핑한다.
 *
 * v0_9 렌더러는 Button 의 variant 를 DOM 에 남기지 않는다(CSS 모듈이 비어 className 이 "undefined").
 * 그래서 CSS 로 primary/borderless 를 구분할 훅이 없다. 대신 스펙에서 variant≠default 인 Button 의
 * 자식 Text 라벨을 뽑아두고, A2uiPanel 이 렌더 후 textContent 가 일치하는 <button> 에 클래스를 부여한다.
 * (라벨이 데이터바인딩이라 정적으로 알 수 없으면 제외. 라벨 중복은 같은 variant 로 태깅되며 실무상 무해.)
 */
export function buttonVariantsByLabel(
  messages: Record<string, unknown>[],
): Record<string, 'primary' | 'borderless'> {
  const byId = new Map<string, any>();
  for (const msg of messages) {
    const uc = (msg as any)?.updateComponents;
    if (uc && Array.isArray(uc.components))
      for (const c of uc.components) if (c && c.id) byId.set(c.id, c);
  }
  const out: Record<string, 'primary' | 'borderless'> = {};
  for (const c of byId.values()) {
    if (!c || c.component !== 'Button') continue;
    if (c.variant !== 'primary' && c.variant !== 'borderless') continue;
    const child = byId.get(c.child);
    const label = child && child.component === 'Text' ? child.text : undefined;
    if (typeof label === 'string' && label.length) out[label] = c.variant;
  }
  return out;
}

/** 값을 데이터모델에 양방향 바인딩하는 입력 컴포넌트들(basicCatalog). */
const INPUT_COMPONENTS = new Set(['TextField', 'CheckBox', 'Slider', 'ChoicePicker', 'DateTimeInput']);

/**
 * A2UI 입력 컴포넌트는 `value` 가 **데이터모델 경로 바인딩**({path})이어야 편집이 반영된다(controlled input +
 * setValue → 그 path 에 기록). LLM 은 흔히 리터럴 value("" 등)를 줘서 입력이 얼어붙는다. 이를 교정:
 *   - 입력 컴포넌트의 리터럴/누락 value → { path: "/<id>" } 바인딩으로 변환(편집 가능해짐).
 *   - createSurface 에 sendDataModel:true 를 켜, 액션 발생 시 입력값(데이터모델)이 서버로 함께 전달되게 한다
 *     (A2uiPanel 이 getClientDataModel() 을 action.context 에 병합).
 * 이미 {path}/함수콜 바인딩이면 건드리지 않는다.
 */
export function normalizeInputBindings(
  messages: Record<string, unknown>[],
): Record<string, unknown>[] {
  return messages.map((msg) => {
    const cs = (msg as any)?.createSurface;
    if (cs && typeof cs === 'object') {
      if (cs.sendDataModel !== true) return { ...msg, createSurface: { ...cs, sendDataModel: true } };
      return msg;
    }
    const uc = (msg as any)?.updateComponents;
    if (!uc || !Array.isArray(uc.components)) return msg;
    let changed = false;
    const components = uc.components.map((c: any) => {
      if (!c || !INPUT_COMPONENTS.has(c.component)) return c;
      const v = c.value;
      const isBinding =
        v != null && typeof v === 'object' && !Array.isArray(v) && ('path' in v || 'call' in v);
      if (isBinding) return c;
      changed = true;
      return { ...c, value: { path: `/${c.id}` } };
    });
    return changed ? { ...msg, updateComponents: { ...uc, components } } : msg;
  });
}

/**
 * Image 의 `fit` 기본값을 `cover` 로 교정한다.
 *
 * basicCatalog 의 Image 기본 fit 은 `fill`(=CSS object-fit:fill)이라 원본 비율을 무시하고 컨테이너에
 * 맞춰 **왜곡**된다. 또 렌더러는 fit 을 인라인 스타일로 박아 넣어 CSS 로 못 덮는다. 대부분의 카드용
 * 사진은 `cover`(비율 유지 + 크롭)가 자연스러우므로, fit 이 없거나 기본값 fill 이면 cover 로 바꾼다.
 * 에이전트가 명시적으로 contain/none/scaleDown 등을 지정했으면 존중한다.
 */
export function normalizeImageFit(messages: Record<string, unknown>[]): Record<string, unknown>[] {
  return messages.map((msg) => {
    const uc = (msg as any)?.updateComponents;
    if (!uc || !Array.isArray(uc.components)) return msg;
    let changed = false;
    const components = uc.components.map((c: any) => {
      if (!c || c.component !== 'Image') return c;
      if (c.fit == null || c.fit === 'fill') {
        changed = true;
        return { ...c, fit: 'cover' };
      }
      return c;
    });
    return changed ? { ...msg, updateComponents: { ...uc, components } } : msg;
  });
}

/**
 * Card 는 basicCatalog 스키마상 자식이 단수(`child`)인데, LLM 이 Column/Row 처럼 `children`(배열)로
 * 잘못 주면 빈 카드로 렌더된다. children 1개면 그대로 child 로, 여러 개면 합성 Column 으로 감싼다.
 */
export function normalizeCardChildren(
  messages: Record<string, unknown>[],
): Record<string, unknown>[] {
  return messages.map((msg) => {
    const uc = (msg as any)?.updateComponents;
    if (!uc || !Array.isArray(uc.components)) return msg;
    const synthesized: any[] = [];
    const components = uc.components.map((c: any) => {
      if (c && c.component === 'Card' && Array.isArray(c.children) && c.child == null) {
        const { children, ...rest } = c;
        if (children.length === 1) return { ...rest, child: children[0] };
        const colId = `${c.id}__col`;
        synthesized.push({ id: colId, component: 'Column', children });
        return { ...rest, child: colId };
      }
      return c;
    });
    if (!synthesized.length && components.every((c: any, i: number) => c === uc.components[i]))
      return msg;
    return { ...msg, updateComponents: { ...uc, components: [...components, ...synthesized] } };
  });
}
